import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import type { ContactDto, ContactListItem } from '@lawyer/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ConfirmDialog } from '../components/common/ConfirmDialog'
import { EmptyState, ErrorAlert, LoadingBlock } from '../components/common/Feedback'
import { PageHeader } from '../components/common/PageHeader'
import { ContactDialog } from '../components/contacts/ContactDialog'
import { api } from '../lib/api'
import { qk } from '../lib/queryClient'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

export function ContactsPage() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ContactDto | null>(null)
  const [pendingDelete, setPendingDelete] = useState<ContactListItem | null>(null)

  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.contacts({ search }),
    queryFn: () => api.get<ContactListItem[]>('/contacts', { search: search || undefined }),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/contacts/${id}`),
    onSuccess: () => {
      setPendingDelete(null)
      void queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })

  return (
    <>
      <PageHeader
        title="אנשי קשר"
        subtitle="עדכון פרטים במקום אחד משתקף בכל הפרויקטים והתיקים"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditing(null)
              setDialogOpen(true)
            }}
          >
            איש קשר חדש
          </Button>
        }
      />

      <TextField
        label="חיפוש"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ maxWidth: 360, mb: 2 }}
      />

      {error ? <ErrorAlert error={error} onRetry={() => void refetch()} /> : null}
      {remove.error ? <ErrorAlert error={remove.error} /> : null}
      {isPending ? <LoadingBlock /> : null}

      {data ? (
        data.length === 0 ? (
          <EmptyState title="אין אנשי קשר" />
        ) : (
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>שם</TableCell>
                  <TableCell>דואר אלקטרוני</TableCell>
                  <TableCell>טלפון</TableCell>
                  <TableCell>פרויקטים</TableCell>
                  <TableCell>תיקים</TableCell>
                  <TableCell align="left" />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((contact) => (
                  <TableRow key={contact.id} hover>
                    <TableCell>{contact.name}</TableCell>
                    <TableCell>
                      {contact.email ? (
                        <Link href={`mailto:${contact.email}`} underline="hover">
                          {contact.email}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{contact.phone || '—'}</TableCell>
                    <TableCell>{contact.projectCount}</TableCell>
                    <TableCell>{contact.caseCount}</TableCell>
                    <TableCell align="left">
                      <Stack direction="row" justifyContent="flex-end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditing(contact)
                            setDialogOpen(true)
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setPendingDelete(contact)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 2 }}>
              מחיקה אפשרית רק כשאיש הקשר אינו מקושר לשום פרויקט או תיק.
            </Typography>
          </Paper>
        )
      ) : null}

      <ContactDialog open={dialogOpen} contact={editing} onClose={() => setDialogOpen(false)} />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="מחיקת איש קשר"
        message={`כרטיס איש הקשר "${pendingDelete?.name ?? ''}" יימחק לצמיתות.`}
        confirmLabel="מחיקה"
        busy={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
        onClose={() => setPendingDelete(null)}
      />
    </>
  )
}
